import React, { useState, useEffect } from 'react';
import { Table, Card, Spin, message, Button, Input, Select, Tag } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import API_BASE_URL from '../config/api';

const { Option } = Select;

// 添加样式
const tableStyles = `
  .vbp-group-header {
    background-color: #1890ff !important;
    color: white !important;
  }
  .vbp-cv-pi-header {
    background-color: #10239e !important;
    color: white !important;
  }
  .vbp-cns-header {
    background-color: #003a8c !important;
    color: white !important;
  }
  .vbp-province-header {
    background-color: #391085 !important;
    color: white !important;
  }
  .vbp-cv-pi-cell,
  .vbp-cns-cell {
    background-color: transparent !important;
  }
  .vbp-quarter-header {
    font-weight: 600;
    color: #0f0f0f !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  .vbp-quarter-header.q4-2025-header {
    background-color: #c8d9ff !important;
  }
  .vbp-quarter-header.q1-2026-header {
    background-color: #d4f5be !important;
  }
  .vbp-quarter-header.q2-2026-header {
    background-color: #ffe3b8 !important;
  }
  .vbp-quarter-header.q3-2026-header {
    background-color: #ffd6e7 !important;
  }
  .vbp-quarter-header.q4-2026-header {
    background-color: #d6e4ff !important;
  }
  .vbp-quarter-cell {
    font-weight: 500;
  }
  .vbp-quarter-cell.q4-2025-cell {
    background-color: #e6f7ff !important;
  }
  .vbp-quarter-cell.q1-2026-cell {
    background-color: #f6ffed !important;
  }
  .vbp-quarter-cell.q2-2026-cell {
    background-color: #fff7e6 !important;
  }
  .vbp-quarter-cell.q3-2026-cell {
    background-color: #fff0f6 !important;
  }
  .vbp-quarter-cell.q4-2026-cell {
    background-color: #f0f5ff !important;
  }
  .vbp-province-cell {
    background-color: #f9f0ff !important;
  }
  .city-cell {
    background-color: #f9f0ff !important;
  }
  .price-policy-header {
    background-color: #52c41a !important;
    color: white !important;
  }
  .price-policy-cell {
    background-color: #f6ffed !important;
  }
  .marked-product-header {
    background-color: #13c2c2 !important;
    color: white !important;
  }
  .marked-red-product-header {
    background-color: #ff4d4f !important;
    color: white !important;
  }
  .unmarked-yellow-product-header {
    background-color: #faad14 !important;
    color: white !important;
  }
  .marked-red-product-cell {
    background-color: #fff1f0 !important;
  }
  .unmarked-yellow-product-cell {
    background-color: #fffbe6 !important;
  }
  .other-policy-header {
    background-color: #fa8c16 !important;
    color: white !important;
  }
  .other-policy-catalog-header {
    background-color: #9254de !important;
    color: white !important;
  }
  .other-policy-payment-header {
    background-color: #1890ff !important;
    color: white !important;
  }
  .other-policy-external-header {
    background-color: #fa541c !important;
    color: white !important;
  }
  .other-policy-catalog-cell {
    background-color: #f4edff !important;
  }
  .other-policy-payment-cell {
    background-color: #e6f7ff !important;
  }
  .other-policy-external-cell {
    background-color: #fff2e8 !important;
  }
`

const DataTable = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterRegion, setFilterRegion] = useState('');

  // 获取数据
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ma-policy', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        // 展开数据：每个案例一行
        const expandedData = [];
        result.forEach(item => {
          // 获取三个模块的案例数量
          const policyCasesCount = item.policy_cases?.length || 0;
          const paymentMethodsCount = item.payment_methods?.length || 0;
          const externalMarketsCount = item.external_markets?.length || 0;
          const maxCases = Math.max(policyCasesCount, paymentMethodsCount, externalMarketsCount, 1);
          
          // 为每个案例创建一行
          for (let i = 0; i < maxCases; i++) {
            expandedData.push({
              ...item,
              _rowIndex: i, // 记录行索引
              _totalRows: maxCases, // 记录总行数
              _policyCase: item.policy_cases?.[i] || null,
              _paymentMethod: item.payment_methods?.[i] || null,
              _externalMarket: item.external_markets?.[i] || null,
              // 生成唯一key
              _uniqueKey: `${item.id}_${i}`
            });
          }
        });
        setData(expandedData);
        setFilteredData(expandedData);
      } else {
        message.error('获取数据失败');
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 搜索和筛选
  useEffect(() => {
    let filtered = data;
    
    if (searchText) {
      filtered = filtered.filter(item => 
        item.ma_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.rma_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.province?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    if (filterRegion) {
      filtered = filtered.filter(item => item.division === filterRegion);
    }
    
    setFilteredData(filtered);
  }, [data, searchText, filterRegion]);

  // 表格列定义 - 使用分组结构
  const columns = [
    // 基础信息列（固定左侧）- 需要合并单元格
    {
      title: '省份',
      dataIndex: 'province',
      key: 'province',
      fixed: 'left',
      width: 100,
      sorter: (a, b) => (a.province || '').localeCompare(b.province || ''),
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
    {
      title: 'MA姓名',
      dataIndex: 'ma_name',
      key: 'ma_name',
      width: 100,
      sorter: (a, b) => (a.ma_name || '').localeCompare(b.ma_name || ''),
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
    {
      title: 'RMA姓名',
      dataIndex: 'rma_name',
      key: 'rma_name',
      width: 100,
      sorter: (a, b) => (a.rma_name || '').localeCompare(b.rma_name || ''),
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
    {
      title: '分区',
      dataIndex: 'division',
      key: 'division',
      width: 80,
      sorter: (a, b) => (a.division || '').localeCompare(b.division || ''),
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
    {
      title: '主责城市',
      dataIndex: 'responsible_city',
      key: 'responsible_city',
      width: 100,
      className: 'city-cell',
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
    {
      title: '审批状态',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 100,
      render: (status) => {
        const statusMap = {
          'pending': { text: '待审批', color: 'orange' },
          'approved': { text: '已通过', color: 'green' },
          'rejected': { text: '已拒绝', color: 'red' },
          'partial': { text: '部分通过', color: 'blue' }
        };
        const statusInfo = statusMap[status] || { text: '无需审批', color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
      sorter: (a, b) => (a.approval_status || '').localeCompare(b.approval_status || ''),
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
    
    // VBP全流程管理 - 分组列（需要合并单元格）
    {
      title: 'VBP全流程管理',
      className: 'vbp-group-header',
      children: [
        {
          title: 'CV领域',
          className: 'vbp-cv-pi-header',
          children: [
            {
              title: '2025年Q4',
              className: 'vbp-quarter-header q4-2025-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'hcv_pi_q4_2025_quantity_ratio',
                  key: 'hcv_pi_q4_2025_quantity_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q4-2025-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '金额比',
                  dataIndex: 'hcv_pi_q4_2025_amount_ratio',
                  key: 'hcv_pi_q4_2025_amount_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q4-2025-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q1',
              className: 'vbp-quarter-header q1-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'hcv_pi_q1_2026_quantity_ratio',
                  key: 'hcv_pi_q1_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q1-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '金额比',
                  dataIndex: 'hcv_pi_q1_2026_amount_ratio',
                  key: 'hcv_pi_q1_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q1-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q2',
              className: 'vbp-quarter-header q2-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'hcv_pi_q2_2026_quantity_ratio',
                  key: 'hcv_pi_q2_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q2-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '金额比',
                  dataIndex: 'hcv_pi_q2_2026_amount_ratio',
                  key: 'hcv_pi_q2_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q2-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q3',
              className: 'vbp-quarter-header q3-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'hcv_pi_q3_2026_quantity_ratio',
                  key: 'hcv_pi_q3_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q3-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '金额比',
                  dataIndex: 'hcv_pi_q3_2026_amount_ratio',
                  key: 'hcv_pi_q3_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q3-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q4',
              className: 'vbp-quarter-header q4-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'hcv_pi_q4_2026_quantity_ratio',
                  key: 'hcv_pi_q4_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q4-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '金额比',
                  dataIndex: 'hcv_pi_q4_2026_amount_ratio',
                  key: 'hcv_pi_q4_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cv-pi-cell vbp-quarter-cell q4-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
          ],
        },
        {
          title: '其他领域（选填）',
          className: 'vbp-cns-header',
          children: [
            {
              title: '2025年Q4',
              className: 'vbp-quarter-header q4-2025-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'cns_q4_2025_quantity_ratio',
                  key: 'cns_q4_2025_quantity_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q4-2025-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '相关领域',
                  dataIndex: 'cns_q4_2025_amount_ratio',
                  key: 'cns_q4_2025_amount_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q4-2025-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q1',
              className: 'vbp-quarter-header q1-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'cns_q1_2026_quantity_ratio',
                  key: 'cns_q1_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q1-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '相关领域',
                  dataIndex: 'cns_q1_2026_amount_ratio',
                  key: 'cns_q1_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q1-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q2',
              className: 'vbp-quarter-header q2-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'cns_q2_2026_quantity_ratio',
                  key: 'cns_q2_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q2-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '相关领域',
                  dataIndex: 'cns_q2_2026_amount_ratio',
                  key: 'cns_q2_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q2-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q3',
              className: 'vbp-quarter-header q3-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'cns_q3_2026_quantity_ratio',
                  key: 'cns_q3_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q3-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '相关领域',
                  dataIndex: 'cns_q3_2026_amount_ratio',
                  key: 'cns_q3_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q3-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
            {
              title: '2026年Q4',
              className: 'vbp-quarter-header q4-2026-header',
              children: [
                {
                  title: '数量比',
                  dataIndex: 'cns_q4_2026_quantity_ratio',
                  key: 'cns_q4_2026_quantity_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q4-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
                {
                  title: '相关领域',
                  dataIndex: 'cns_q4_2026_amount_ratio',
                  key: 'cns_q4_2026_amount_ratio',
                  width: 80,
                  className: 'vbp-cns-cell vbp-quarter-cell q4-2026-cell',
                  onCell: (record) => ({
                    rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
                  }),
                },
              ],
            },
          ],
        },

      ],
    },
    
    // 红黄标管控政策 - 分组列（需要合并单元格）
    {
      title: '红黄标管控政策',
      className: 'price-policy-header',
      children: [
        {
          title: '标红产品',
          className: 'marked-red-product-header',
          children: [
            {
              title: '立普妥',
              dataIndex: 'marked_red_products',
              key: 'marked_red_product_1',
              width: 80,
              className: 'marked-red-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('立普妥') ? '立普妥' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '络活喜',
              dataIndex: 'marked_red_products',
              key: 'marked_red_product_2',
              width: 80,
              className: 'marked-red-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('络活喜') ? '络活喜' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '西乐葆',
              dataIndex: 'marked_red_products',
              key: 'marked_red_product_3',
              width: 80,
              className: 'marked-red-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('西乐葆') ? '西乐葆' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '乐瑞卡',
              dataIndex: 'marked_red_products',
              key: 'marked_red_product_4',
              width: 80,
              className: 'marked-red-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('乐瑞卡') ? '乐瑞卡' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '左洛复',
              dataIndex: 'marked_red_products',
              key: 'marked_red_product_5',
              width: 80,
              className: 'marked-red-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('左洛复') ? '左洛复' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '怡诺思',
              dataIndex: 'marked_red_products',
              key: 'marked_red_product_6',
              width: 80,
              className: 'marked-red-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('怡诺思') ? '怡诺思' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
          ]
        },
        {
          title: '未被标黄产品',
          className: 'unmarked-yellow-product-header',
          children: [
            {
              title: '立普妥',
              dataIndex: 'unmarked_yellow_products',
              key: 'unmarked_yellow_product_1',
              width: 80,
              className: 'unmarked-yellow-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('立普妥') ? '立普妥' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '络活喜',
              dataIndex: 'unmarked_yellow_products',
              key: 'unmarked_yellow_product_2',
              width: 80,
              className: 'unmarked-yellow-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('络活喜') ? '络活喜' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '西乐葆',
              dataIndex: 'unmarked_yellow_products',
              key: 'unmarked_yellow_product_3',
              width: 80,
              className: 'unmarked-yellow-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('西乐葆') ? '西乐葆' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '乐瑞卡',
              dataIndex: 'unmarked_yellow_products',
              key: 'unmarked_yellow_product_4',
              width: 80,
              className: 'unmarked-yellow-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('乐瑞卡') ? '乐瑞卡' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '左洛复',
              dataIndex: 'unmarked_yellow_products',
              key: 'unmarked_yellow_product_5',
              width: 80,
              className: 'unmarked-yellow-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('左洛复') ? '左洛复' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
            {
              title: '怡诺思',
              dataIndex: 'unmarked_yellow_products',
              key: 'unmarked_yellow_product_6',
              width: 80,
              className: 'unmarked-yellow-product-cell',
              render: (text) => {
                const products = Array.isArray(text) ? text : (text ? text.split(',') : []);
                return products.includes('怡诺思') ? '怡诺思' : '';
              },
              onCell: (record) => ({
                rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
              }),
            },
          ]
        },
        {
          title: '管理方案',
          dataIndex: 'management_plan',
          key: 'management_plan',
          width: 120,
          className: 'price-policy-cell',
          onCell: (record) => ({
            rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
          }),
        },
        {
          title: '是否有明确政策发文',
          dataIndex: 'has_policy_document',
          key: 'has_policy_document',
          width: 140,
          className: 'price-policy-cell',
          onCell: (record) => ({
            rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
          }),
        },
        {
          title: '是否有官方明确澄清（不考核分子式）',
          dataIndex: 'clarified_total_amount_assessment',
          key: 'clarified_total_amount_assessment',
          width: 140,
          className: 'price-policy-cell',
          onCell: (record) => ({
            rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
          }),
        },
        {
          title: '是否改善弹窗警示内容',
          dataIndex: 'price_policy_2',
          key: 'price_policy_2',
          width: 160,
          className: 'price-policy-cell',
          onCell: (record) => ({
            rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
          }),
        },
        {
          title: '红标金额比例',
          dataIndex: 'amount_ratio_red',
          key: 'amount_ratio_red',
          width: 110,
          className: 'price-policy-cell',
          onCell: (record) => ({
            rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
          }),
        },
        {
          title: '红黄标金额比例',
          dataIndex: 'amount_ratio_red_yellow',
          key: 'amount_ratio_red_yellow',
          width: 120,
          className: 'price-policy-cell',
          onCell: (record) => ({
            rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
          }),
        },
        {
          title: '补充说明（是否有其他政策突破性的优化考核特例）',
          dataIndex: 'explanation_dimension',
          key: 'explanation_dimension',
          width: 150,
          className: 'price-policy-cell',
          onCell: (record) => ({
            rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
          }),
        },
      ],
    },
    
    // 其他政策空间 - 分组列（每个案例一行，不合并）
    {
      title: '其他政策空间',
      className: 'other-policy-header',
      children: [
        {
          title: '目录准入',
          className: 'other-policy-catalog-header',
          children: [
            {
              title: '案例',
              dataIndex: '_policyCase',
              key: 'policy_case_name',
              width: 150,
              className: 'other-policy-catalog-cell',
              render: (policyCase) => policyCase?.case_name || '-',
            },
            {
              title: '既往政策内容',
              dataIndex: '_policyCase',
              key: 'policy_case_past_policy',
              width: 180,
              className: 'other-policy-catalog-cell',
              render: (policyCase) => policyCase?.past_policy || '-',
            },
            {
              title: '政策更新点',
              dataIndex: '_policyCase',
              key: 'policy_case_policy_update',
              width: 180,
              className: 'other-policy-catalog-cell',
              render: (policyCase) => policyCase?.policy_update || '-',
            },
            {
              title: '涉及业务产品',
              dataIndex: '_policyCase',
              key: 'policy_case_related_products',
              width: 180,
              className: 'other-policy-catalog-cell',
              render: (policyCase) => {
                if (!policyCase) return '-';
                return Array.isArray(policyCase.related_products) 
                  ? policyCase.related_products.join(', ') 
                  : (policyCase.related_products || '-');
              },
            },
            {
              title: 'RMA赋分',
              dataIndex: '_policyCase',
              key: 'policy_case_rma_score',
              width: 150,
              className: 'other-policy-catalog-cell',
              render: (policyCase) => policyCase?.rma_score || '-',
            },
          ],
        },
        {
          title: '支付方式',
          className: 'other-policy-payment-header',
          children: [
            {
              title: '案例',
              dataIndex: '_paymentMethod',
              key: 'payment_method_case_name',
              width: 150,
              className: 'other-policy-payment-cell',
              render: (paymentMethod) => paymentMethod?.case_name || '-',
            },
            {
              title: '既往政策内容',
              dataIndex: '_paymentMethod',
              key: 'payment_method_past_policy',
              width: 180,
              className: 'other-policy-payment-cell',
              render: (paymentMethod) => paymentMethod?.past_policy || '-',
            },
            {
              title: '政策更新点',
              dataIndex: '_paymentMethod',
              key: 'payment_method_policy_update',
              width: 180,
              className: 'other-policy-payment-cell',
              render: (paymentMethod) => paymentMethod?.policy_update || '-',
            },
            {
              title: '涉及业务产品',
              dataIndex: '_paymentMethod',
              key: 'payment_method_related_products',
              width: 180,
              className: 'other-policy-payment-cell',
              render: (paymentMethod) => {
                if (!paymentMethod) return '-';
                return Array.isArray(paymentMethod.related_products) 
                  ? paymentMethod.related_products.join(', ') 
                  : (paymentMethod.related_products || '-');
              },
            },
            {
              title: 'RMA赋分',
              dataIndex: '_paymentMethod',
              key: 'payment_method_rma_score',
              width: 150,
              className: 'other-policy-payment-cell',
              render: (paymentMethod) => paymentMethod?.rma_score || '-',
            },
          ],
        },
        {
          title: '院外市场',
          className: 'other-policy-external-header',
          children: [
            {
              title: '案例',
              dataIndex: '_externalMarket',
              key: 'external_market_case_name',
              width: 150,
              className: 'other-policy-external-cell',
              render: (externalMarket) => externalMarket?.case_name || '-',
            },
            {
              title: '既往政策内容',
              dataIndex: '_externalMarket',
              key: 'external_market_past_policy',
              width: 180,
              className: 'other-policy-external-cell',
              render: (externalMarket) => externalMarket?.past_policy || '-',
            },
            {
              title: '政策更新点',
              dataIndex: '_externalMarket',
              key: 'external_market_policy_update',
              width: 180,
              className: 'other-policy-external-cell',
              render: (externalMarket) => externalMarket?.policy_update || '-',
            },
            {
              title: '涉及业务产品',
              dataIndex: '_externalMarket',
              key: 'external_market_related_products',
              width: 180,
              className: 'other-policy-external-cell',
              render: (externalMarket) => {
                if (!externalMarket) return '-';
                return Array.isArray(externalMarket.related_products) 
                  ? externalMarket.related_products.join(', ') 
                  : (externalMarket.related_products || '-');
              },
            },
            {
              title: 'RMA赋分',
              dataIndex: '_externalMarket',
              key: 'external_market_rma_score',
              width: 150,
              className: 'other-policy-external-cell',
              render: (externalMarket) => externalMarket?.rma_score || '-',
            },
          ],
        },
      ],
    },
    
    // 时间信息列（需要合并单元格）
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
      sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
      onCell: (record) => ({
        rowSpan: record._rowIndex === 0 ? record._totalRows : 0,
      }),
    },
  ];

  // 导出数据
  const handleExport = async () => {
    console.log('开始导出数据...');
    try {
      const token = localStorage.getItem('token');
      console.log('Token存在:', !!token);
      
      const url = `${API_BASE_URL}/api/export/excel`;
      console.log('请求URL:', url);
      
      // 调用后端API下载Excel
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('响应状态:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('响应错误:', errorText);
        throw new Error('导出失败');
      }
      
      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `MA_Policy_Data_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }
      
      console.log('下载文件名:', fileName);
      
      // 下载文件
      const blob = await response.blob();
      console.log('Blob大小:', blob.size);
      saveAs(blob, fileName);
      
      message.success('数据已导出');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    }
  };

  return (
    <>
      <style>{tableStyles}</style>
      <Card 
        title="数据总览表格" 
        extra={
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Input
            placeholder="搜索MA/RMA/省份"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="筛选分区"
            value={filterRegion}
            onChange={setFilterRegion}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="北区">北区</Option>
            <Option value="东区">东区</Option>
            <Option value="南区">南区</Option>
            <Option value="中区">中区</Option>
          </Select>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={handleExport}
          >
            导出
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_uniqueKey"
          scroll={{ x: 6500, y: 600 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100'],
            defaultPageSize: 20,
          }}
          size="small"
          bordered
        />
      </Spin>
    </Card>
    </>
  );
};

export default DataTable;